/**
 * Admin wizard entry detail page for /admin/wizard-entries/:entryId route.
 * Shows full wizard instance data including steps and collected data.
 */

import { useState } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { PageHeader } from "../components/layout/PageHeader";
import { KiiDialogLayout } from "../components/kii";
import { useProfile } from "../contexts/ProfileContext";
import {
  getWizardInstanceDetail,
  deleteWizardInstance,
  adminGetUserDetail,
  adminGetSettings,
  sendWizardInvite,
  type WizardInstanceDetail,
} from "../services/naraDoor";
import { adminRoutes, publicRoutes } from "../lib/routes";
import {
  RefreshCw,
  AlertCircle,
  User,
  Trash2,
  Loader2,
  AlertTriangle,
  Mail,
  Check,
} from "lucide-react";

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  abandoned: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  timed_out: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  pending: "bg-muted text-muted-foreground",
  waiting: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  executing: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  skipped: "bg-muted text-muted-foreground",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || "bg-muted text-muted-foreground";
  const label = status.replace(/_/g, " ");
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${style}`}>
      {label}
    </span>
  );
}

export function AdminWizardEntryDetail() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { entryId } = useParams<{ entryId: string }>();
  const { isAdmin, isLoading: isProfileLoading, profile } = useProfile();

  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Send invite state
  const [inviteStatus, setInviteStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [inviteError, setInviteError] = useState<string | null>(null);

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const {
    data: instance,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["wizardInstanceDetail", entryId],
    queryFn: () => getWizardInstanceDetail(entryId!),
    enabled: isAdmin && !!entryId,
  });

  const handleSendInvite = async () => {
    if (!instance) return;
    setInviteStatus("sending");
    setInviteError(null);

    try {
      // Fetch user details to get email
      const user = await adminGetUserDetail(instance.user_id);
      if (!user.email) {
        throw new Error("User has no email address");
      }

      // Load email templates from admin settings
      const DEFAULT_TEMPLATES = {
        emailSubject: "{{ sender.name }} invited you to complete a step",
        emailTemplate:
          'Hi {{ recipient.name }},\n\n{{ sender.name }} would like you to complete the "{{ step_name }}" step.\n\nClick here to continue: {{ wizard_link }}\n\nBest regards,\nThe Nara Team',
      };

      let emailTemplate = DEFAULT_TEMPLATES.emailTemplate;

      try {
        const settings = await adminGetSettings();
        if (settings?.wizardInviteTemplates?.emailTemplate) {
          emailTemplate = settings.wizardInviteTemplates.emailTemplate;
        }
      } catch {
        // Use defaults
      }

      const recipientName = instance.user_display_name || "there";
      const senderName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || "Admin";
      const wizardLink = `${window.location.origin}/wizard/${instance.instance_id}`;
      const stepName = instance.template_name || "wizard";

      const renderedMessage = emailTemplate
        .replace(/\{\{\s*recipient\.name\s*\}\}/g, recipientName)
        .replace(/\{\{\s*sender\.name\s*\}\}/g, senderName)
        .replace(/\{\{\s*wizard_link\s*\}\}/g, wizardLink)
        .replace(/\{\{\s*step_name\s*\}\}/g, stepName);

      await sendWizardInvite({
        recipientUserId: instance.user_id,
        channel: "email",
        message: renderedMessage,
        wizardLink,
        recipientEmail: user.email,
      });

      setInviteStatus("sent");
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : String(err));
      setInviteStatus("error");
    }
  };

  return (
    <div>
      <PageHeader
        title={instance ? `Entry: ${instance.template_name}` : "Wizard Entry"}
        description={entryId}
        isAdmin
        breadcrumbs={[
          { label: "Dashboard", href: publicRoutes.home },
          { label: "Admin" },
          { label: "Wizards", href: adminRoutes.wizards },
          { label: instance?.template_name || "...", href: instance ? adminRoutes.wizard(instance.template_id) : undefined },
          { label: "Entry" },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={handleSendInvite}
              disabled={inviteStatus === "sending" || inviteStatus === "sent"}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inviteStatus === "sending" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : inviteStatus === "sent" ? (
                <Check className="h-4 w-4" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {inviteStatus === "sending" ? "Sending..." : inviteStatus === "sent" ? "Invite Sent" : "Send Email Invite"}
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => { setDeleteError(null); setShowDeleteDialog(true); }}
              className="inline-flex items-center gap-2 px-3 py-2 text-destructive border border-destructive/30 rounded-lg hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        }
      />

      {inviteStatus === "error" && inviteError && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          Failed to send invite: {inviteError}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800 dark:text-red-200">Failed to load entry</p>
            <p className="text-sm text-red-600">{(error as Error).message}</p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          <div className="h-32 bg-muted rounded-lg animate-pulse" />
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
        </div>
      ) : instance && (
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-4">Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Instance ID</span>
                <p className="font-mono text-xs mt-0.5">{instance.instance_id}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Template</span>
                <p className="font-medium mt-0.5">{instance.template_name}</p>
              </div>
              <div>
                <span className="text-muted-foreground">User</span>
                <p className="mt-0.5">
                  <Link
                    to={adminRoutes.user(instance.user_id)}
                    className="text-nara-orange hover:underline inline-flex items-center gap-1"
                  >
                    <User className="h-3.5 w-3.5" />
                    {instance.user_display_name || instance.user_id}
                  </Link>
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Status</span>
                <p className="mt-1"><StatusBadge status={instance.status} /></p>
              </div>
              <div>
                <span className="text-muted-foreground">Started</span>
                <p className="mt-0.5">{instance.started_at ? formatDate(instance.started_at, "datetime") : "\u2014"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Completed</span>
                <p className="mt-0.5">{instance.completed_at ? formatDate(instance.completed_at, "datetime") : "\u2014"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total XP</span>
                <p className="mt-0.5 font-medium">{instance.total_xp ?? "\u2014"}</p>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-4">Steps</h4>
            {(() => {
              const snapshotSteps = (instance.template_snapshot?.steps as any[]) || [];
              const pointsMap: Record<string, number> = {};
              snapshotSteps.forEach((s: any) => { pointsMap[s.step_id] = s.config?.points || 0; });
              return (
            <div className="space-y-3">
              {(instance.steps || []).map((step, index) => (
                <div key={step.step_id} className="border border-border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                    <span className="text-sm font-mono">{step.block_id}</span>
                    <StatusBadge status={step.status} />
                    {pointsMap[step.step_id] > 0 && (
                      <span className="text-xs px-1.5 py-0.5 bg-nara-orange/10 text-nara-orange rounded font-medium">
                        {pointsMap[step.step_id]} XP
                      </span>
                    )}
                    {step.completed_at && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {formatDate(step.completed_at, "datetime")}
                      </span>
                    )}
                  </div>
                  {step.error && (
                    <div className="mt-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded">
                      {step.error}
                    </div>
                  )}
                  {(() => {
                    const snapshot = snapshotSteps.find((s: any) => s.step_id === step.step_id);
                    const showLabeled = snapshot?.config?.showInEntryDetail;
                    const fields: any[] = snapshot?.config?.fields || [];
                    const producedKeys: string[] = snapshot?.produced_data || [];
                    const collected = instance.collected_data || {};

                    if (showLabeled && (fields.length > 0 || producedKeys.length > 0)) {
                      const entries: { label: string; value: any }[] = [];
                      for (const field of fields) {
                        entries.push({
                          label: field.label ? t(field.label) : field.name,
                          value: collected[field.name],
                        });
                      }
                      for (const key of producedKeys) {
                        if (!fields.some((f: any) => f.name === key)) {
                          entries.push({ label: key, value: collected[key] });
                        }
                      }
                      return entries.length > 0 ? (
                        <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                          {entries.map((entry, i) => (
                            <div key={i} className="contents">
                              <span className="text-xs font-medium text-muted-foreground">{entry.label}:</span>
                              <span className="text-xs text-foreground">
                                {entry.value != null
                                  ? typeof entry.value === "object"
                                    ? JSON.stringify(entry.value)
                                    : String(entry.value)
                                  : <span className="text-muted-foreground italic">—</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    }

                    // Default: raw JSON dump
                    return step.data && Object.keys(step.data).length > 0 ? (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-muted-foreground">Data:</span>
                        <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-x-auto">
                          {JSON.stringify(step.data, null, 2)}
                        </pre>
                      </div>
                    ) : null;
                  })()}
                </div>
              ))}
              {(!instance.steps || instance.steps.length === 0) && (
                <p className="text-sm text-muted-foreground">No steps recorded</p>
              )}
            </div>
              );
            })()}
          </div>

          {/* Collected Data */}
          {instance.collected_data && Object.keys(instance.collected_data).length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="text-sm font-semibold text-foreground mb-4">Collected Data</h4>
              <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
                {JSON.stringify(instance.collected_data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <KiiDialogLayout
        open={showDeleteDialog}
        onOpenChange={(open) => { if (!open) setShowDeleteDialog(false); }}
        title="Delete Wizard Entry"
        description="Permanently delete this wizard entry? This action cannot be undone."
        size="sm"
        footer={
          <>
            <button
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!entryId) return;
                setIsDeleting(true);
                setDeleteError(null);
                try {
                  await deleteWizardInstance(entryId);
                  queryClient.invalidateQueries({ queryKey: ["wizardInstances"] });
                  setShowDeleteDialog(false);
                  navigate(-1);
                } catch (err) {
                  setDeleteError(err instanceof Error ? err.message : String(err));
                } finally {
                  setIsDeleting(false);
                }
              }}
              disabled={isDeleting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-destructive hover:bg-destructive/90 rounded-lg transition-colors disabled:opacity-50"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </>
        }
      >
        <div className="flex items-start gap-3 mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm text-destructive">
            This will permanently delete this wizard entry and terminate any running workflow. This cannot be undone.
          </div>
        </div>
        {deleteError && (
          <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {deleteError}
          </div>
        )}
      </KiiDialogLayout>
    </div>
  );
}
